import { useEffect, useMemo, useRef, useState } from 'react'

import { PointArray } from '@editor/interfaces/attribute'
import { LineConfig, PolylineLabelProps, PolylineProps } from '@editor/interfaces/canvas'
import { SubtoolVertex } from '@editor/interfaces/tool'
import { useToolUtilSelector, useVertexSelector } from '@editor/store/selectors'
import { merge } from 'lodash'

import { DEFAULT_LABEL_CONFIG, DEFAULT_LINE_CONFIG, DEFAULT_OUTLINE_CONFIG } from '../constants'

const useConfigs = ({
  id,
  points,
  label,
  segmentLabels,
  line,
  lineOutline,
  visible = true,
  opacity = 1,
  isInteractable = true,
  vertexAnchors,
}: PolylineProps) => {
  // Store
  const hoveredElementId = useToolUtilSelector((state) => state.hoveredElementId)
  const selectedElementIds = useToolUtilSelector((state) => state.selectedElementIds)
  const currentVertexSubtool = useVertexSelector((state) => state.currentVertexSubtool)

  // States
  const [isHoverSelected, setIsHoverSelected] = useState(false)
  const [isDraggable, setIsDraggable] = useState(true)
  const [isEndsOnly, setIsEndsOnly] = useState(vertexAnchors?.endsOnly ?? false)
  const [pointOnLine, setPointOnLine] = useState<PointArray>()
  const [pointIndexForMerge, setPointIndexForMerge] = useState<number | null>(null) // Index of point selected for merging polylines

  // Refs
  const dragStartPosRef = useRef<PointArray | null>(null)
  const currentPositionsRef = useRef<PointArray[] | null>(null)
  const isDraggingRef = useRef(false)
  const isDraggingVertexRef = useRef(false)
  const isHoveringAnchorRef = useRef(false)
  const editingAnchorRef = useRef<number | null>(null)

  // Derived
  const isRemovingVertex = currentVertexSubtool === SubtoolVertex.RemoveVertex
  const isMergingPolyline = currentVertexSubtool === SubtoolVertex.ConnectVertex
  const isVertexRemovable = isRemovingVertex && points.length > 2
  const isAddingVertex = currentVertexSubtool === SubtoolVertex.AddVertex
  const isNoVertexActions = !isVertexRemovable && !isDraggable && !isMergingPolyline

  // Configurations for the label, line, and outline
  const labelConfig = useMemo((): PolylineLabelProps | null => {
    if (!label) return null
    return merge({}, DEFAULT_LABEL_CONFIG, label) as PolylineLabelProps
  }, [label])

  const segmentLabelsConfig = useMemo((): PolylineProps['segmentLabels'] | null => {
    if (!segmentLabels) return null
    return merge({}, DEFAULT_LABEL_CONFIG, segmentLabels) as PolylineProps['segmentLabels']
  }, [segmentLabels])

  const lineConfig = useMemo(
    () => (line ? (merge({}, DEFAULT_LINE_CONFIG, line) as Required<LineConfig>) : null),
    [line],
  )

  const lineOutlineConfig = useMemo(
    () => (lineOutline ? (merge({}, DEFAULT_OUTLINE_CONFIG, lineOutline) as Required<LineConfig>) : null),
    [lineOutline],
  )

  // If it can be seen, it can be interacted with
  const canBeSeen = useMemo(
    () => (visible === undefined || visible) && (opacity === undefined || opacity > 0),
    [visible, opacity],
  )
  const canBeInteracted = canBeSeen && isInteractable && !isDraggingRef.current && !editingAnchorRef.current
  const canLineBeInteracted = canBeInteracted && !isHoveringAnchorRef.current

  /**
   * Update hover/selected state
   */
  useEffect(() => {
    setIsHoverSelected(hoveredElementId === id || selectedElementIds.includes(id))
  }, [hoveredElementId, selectedElementIds, id])

  /**
   * Disable dragging if current vertex subtool is not none and there are available subtools.
   * Alo disables pointForMerge when no longer in merging mode.
   */
  useEffect(() => {
    setIsDraggable(!isRemovingVertex && !isMergingPolyline)
    if (!isMergingPolyline) setPointIndexForMerge(null)
  }, [isRemovingVertex, isMergingPolyline])

  /**
   * Update endsOnly state when vertexAnchors prop changes.
   */
  useEffect(() => {
    setIsEndsOnly((vertexAnchors?.endsOnly ?? false) || isMergingPolyline)
  }, [vertexAnchors?.endsOnly, isMergingPolyline])

  return {
    labelConfig,
    segmentLabelsConfig,
    lineConfig,
    lineOutlineConfig,

    isNoVertexActions,
    isHoverSelected,
    isHoveringAnchorRef,
    isEndsOnly,
    isAddingVertex,
    isRemovingVertex,
    isVertexRemovable,
    isMergingPolyline,
    isDraggable,
    isDraggingRef,
    isDraggingVertexRef,
    dragStartPosRef,
    currentPositionsRef,
    editingAnchorRef,

    pointOnLine,
    setPointOnLine,

    pointIndexForMerge,
    setPointIndexForMerge,

    canBeSeen,
    canBeInteracted,
    canLineBeInteracted,
  }
}

export default useConfigs
